import { Suspense, lazy, type ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuthContext } from './context/AuthContext';
import { LoginPage } from './pages/LoginPage';

const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage }))
);

const FullscreenLoader = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <CircularProgress />
  </Box>
);

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { status } = useAuthContext();

  if (status === 'checking') {
    return <FullscreenLoader />;
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  return <Suspense fallback={<FullscreenLoader />}>{children}</Suspense>;
};

const LoginRouteGuard = () => {
  const { status } = useAuthContext();

  if (status === 'authenticated' || status === 'bypassed') {
    return <Navigate to="/" replace />;
  }

  if (status === 'checking') {
    return <FullscreenLoader />;
  }

  return <LoginPage />;
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginRouteGuard />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;